import { useQuery } from '@tanstack/react-query';

import { listQueryOptions } from '@/lib/query-client';
import { makeQueryKeys } from '@/lib/query-keys';

import { chatService } from '../services/chatService';
import type { ChatSessionStatus } from '../types/schemas';

export const chatKeys = makeQueryKeys('chat-sessions');

/** Key branch for a conversation's message page. Prefix-invalidated by the
 *  `chat.message` / `chat.session-changed` realtime listeners. */
export function chatMessagesKey(chatSessionId: string) {
  return chatKeys.operation('messages', chatSessionId);
}

export interface ChatSessionListParams {
  status?: ChatSessionStatus;
  /** Location watch-filter: only chats at these locations. Omitted / empty =
   *  all the caller's assigned locations. Part of the query key, so changing
   *  the selection refetches. */
  locationIds?: string[];
}

interface UseChatSessionsOptions {
  enabled?: boolean;
}

/**
 * Role-scoped chat list (client: own chats; psy/chief: assigned locations).
 * Newest activity first per the BE ordering. Kept fresh by the chat realtime
 * listeners plus the reconnect self-heal.
 */
export function useChatSessions(
  params: ChatSessionListParams = {},
  options: UseChatSessionsOptions = {},
) {
  const { enabled = true } = options;
  return useQuery({
    queryKey: chatKeys.list(params),
    queryFn: () => chatService.list(params),
    enabled,
    ...listQueryOptions,
  });
}

/** Sorted, de-duped copy of a location-id list, for a stable query key that
 *  doesn't refetch just because the selection's order changed. */
export function normalizeLocationIds(ids: Iterable<string>): string[] {
  return [...new Set(ids)].sort();
}
